import classNames from 'classnames';
import isEmpty from 'lodash.isempty';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import NoServiceAccounts from 'src/assets/no-service-accounts.svg';
import { removeModal } from 'src/features/globalSlice';
import {
  bulkEditAutoAssign,
  getServiceAccountList,
  setServiceAccountList,
} from 'src/store/actions/serviceAccountActions';
import { Modal } from 'src/store/models/Modal';
import { ServiceAccount } from 'src/store/models/ServiceAccount';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useNonInitialEffect } from 'src/utils/useNonInitialEffect';
import { useResizeObserver } from 'src/utils/useResizeObserver';

import Button from '../Button/Button';
import EmptyState from '../EmptyState/EmptyState';
import Table from '../Table/Table';
import {
  Order,
  TableHeader,
  TableSortParameters,
} from '../Table/Table.interface';
import { determineNextOrder } from '../Table/utils/TableHelperFunctions';
import classes from './AutoAssignModal.module.scss';

export interface AutoAssignChange {
  [key: string]: boolean;
}

export interface AutoAssignModalProps {
  modal?: Modal;
}

const AutoAssignModal: FC<AutoAssignModalProps> = ({ modal }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [sortParams, setSortParams] = useState<
    TableSortParameters<ServiceAccount>
  >({});
  const [selectedRows, setSelectedRows] = useState<ServiceAccount[]>([]);
  const [changedAccounts, setChangedAccounts] =
    useState<AutoAssignChange>(null);
  const [serviceAccounts, setServiceAccounts] = useState<ServiceAccount[]>([]);

  const serviceAccountList = useAppSelector(
    (state) => state.serviceAccount.serviceAccountList
  );
  const loading = useLoadingSelector({
    actionType: getServiceAccountList.type,
  });

  useEffect(() => {
    dispatch(setServiceAccountList({ count: 0, data: [], ctx: null }));
    dispatch(
      getServiceAccountList({
        params: {
          size: 1000,
          q: search,
          key: 'title',
          mode: Order.ASC,
        },
      })
    );
    return () => {
      dispatch(setServiceAccountList({ count: 0, data: [], ctx: null }));
    };
    // eslint-disable-next-line
  }, []);

  useNonInitialEffect(() => {
    dispatch(
      getServiceAccountList({
        params: {
          size: 1000,
          q: search,
          key: sortParams?.key,
          mode: sortParams?.mode,
        },
      })
    );
    // eslint-disable-next-line
  }, [search, sortParams]);

  useEffect(() => {
    setServiceAccounts(
      serviceAccountList?.data
        ?.filter((s) => s?.enabled)
        ?.sort((a, b) => {
          if (a?.autoAssign === b?.autoAssign || !isEmpty(sortParams)) return 0;
          if (
            (a?.autoAssign &&
              typeof changedAccounts?.[a?.id] === 'undefined') ||
            changedAccounts?.[a?.id]
          )
            return -1;
          return 1;
        }) || []
    );
    // eslint-disable-next-line
  }, [serviceAccountList]);

  useEffect(() => {
    setSelectedRows([]);
    serviceAccounts?.forEach((s) => {
      if (
        (s?.autoAssign && typeof changedAccounts?.[s?.id] === 'undefined') ||
        changedAccounts?.[s?.id]
      ) {
        setSelectedRows((prev) => [...prev, s]);
      }
    });
    // eslint-disable-next-line
  }, [serviceAccounts]);

  const handleCheckboxChange = (checked: boolean, data: ServiceAccount) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, data]);
    } else {
      setSelectedRows((prev) => prev.filter((row) => row.id !== data.id));
    }
    if (
      serviceAccounts?.find((s) => s?.id === data?.id)?.autoAssign !== checked
    ) {
      setChangedAccounts((prev) => {
        return { ...prev, [data?.id]: checked };
      });
    } else {
      setChangedAccounts((prev) => {
        const copy = { ...prev };
        delete copy[data?.id];
        return copy;
      });
    }
  };

  const onSortClick = (sortKey: keyof ServiceAccount | string) => {
    const orderByToBeSaved = determineNextOrder(sortParams, sortKey);
    setSortParams({
      key: sortKey,
      mode: orderByToBeSaved,
    });
  };

  const setBodyHeight = useCallback(() => {
    const header = modalRef?.current?.querySelector('header');
    const body = modalRef?.current?.querySelector<HTMLDivElement>('section');
    const footer = modalRef?.current?.querySelector('footer');

    if (header && body?.style && footer) {
      body.style.maxHeight = `calc(100vh - ${
        modalRef?.current?.getBoundingClientRect?.()?.height -
        body?.getBoundingClientRect?.()?.height
      }px - 5rem)`;
    }
  }, []);

  const headers: TableHeader<ServiceAccount>[] = [
    {
      accessor: 'title',
      label: t('common:name'),
      sortable: true,
    },
    {
      accessor: 'company',
      label: t('common:company'),
      sortable: true,
      sortKey: 'company.title',
      component: (account) => account?.company?.title,
    },
  ];

  useResizeObserver({
    element: document?.firstElementChild,
    callback: setBodyHeight,
    domRectProperty: 'height',
  });

  return (
    <div className={classes.Container} ref={modalRef}>
      <header>
        <h4>{t('service_account:edit_auto_assign')}</h4>
        <p>{t('service_account:edit_auto_assign_details')}</p>
      </header>
      <section>
        <Table
          headers={headers}
          data={serviceAccounts}
          search
          loading={loading}
          className={classNames(classes.Table, { [classes.Loading]: loading })}
          classes={{ header: classes.TableHeader }}
          emptyTableComponent={
            <EmptyState
              title={t('service_account:no_accounts')}
              subtitle={t('service_account:no_accounts_details')}
              icon={NoServiceAccounts}
            />
          }
          searchPlaceholder={t('common:search_field')}
          onSearchChange={setSearch}
          selectable
          handleCheckboxChange={handleCheckboxChange}
          isSelected={(s) => selectedRows?.some((r) => r?.id === s?.id)}
          selectedRows={selectedRows}
          onSortClick={onSortClick}
          sortParams={sortParams}
        />
      </section>
      <footer>
        <Button
          className={classes.Secondary}
          onClick={() => dispatch(removeModal())}
          variant="outlined"
        >
          {t('common:discard_changes')}
        </Button>
        <Button
          className={classes.Primary}
          disabled={isEmpty(changedAccounts)}
          onClick={() => {
            dispatch(bulkEditAutoAssign(changedAccounts));
          }}
        >
          {t('common:save_changes')}
        </Button>
      </footer>
    </div>
  );
};

export default AutoAssignModal;
